import { useState, type FormEvent } from 'react';

import { Modal } from '@/components/Modal';

/**
 * **지우기 전에 본인 비밀번호를 한 번 더 받는다** (사용자 지시 2026-09-03).
 *
 * 같은 날 주간·월간업무를 전 직원에게 열었다. 등록·수정은 잘못돼도 다음 사람이 고쳐 쓰지만
 * **삭제는 되돌릴 데가 없다** — 권한으로 좁히는 대신 손이 한 번 멈추게 한다.
 *
 * 서버도 같은 값을 대조한다(`PasswordConfirmation`). 화면만 묻고 서버가 안 보면 그것은 확인이
 * 아니라 연출이라, 삭제 요청 본문에 비밀번호를 실어 보낸다.
 *
 * @param error 서버가 돌려준 사유(비밀번호 불일치 등). **모달을 닫지 않고** 이 자리에 그대로 띄운다 —
 *   한 번 틀렸다고 창이 닫히면 사람은 방금 무엇이 잘못됐는지 모른 채 처음부터 다시 한다.
 */
export function PasswordConfirmModal({
  title = '삭제 확인',
  message,
  confirmLabel = '삭제',
  isPending = false,
  error = null,
  onConfirm,
  onClose,
}: {
  title?: string;
  /** 무엇을 지우는지. 제목만으로는 어느 줄을 눌렀는지 알 수 없다. */
  message: string;
  confirmLabel?: string;
  isPending?: boolean;
  error?: string | null;
  onConfirm: (password: string) => void;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!password.trim() || isPending) return;
    onConfirm(password);
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      maxWidth="380px"
      closeDisabled={isPending}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-secondary" disabled={isPending}>
            취소
          </button>
          <button
            type="submit"
            form="password-confirm-form"
            className="btn-danger-lg"
            disabled={isPending || !password.trim()}
          >
            {isPending ? '처리 중…' : confirmLabel}
          </button>
        </>
      }
    >
      {/* 폼으로 감싼다 — 비밀번호를 치고 엔터를 누르는 것이 이 창에서 가장 자연스러운 동작이다. */}
      <form id="password-confirm-form" onSubmit={submit} className="space-y-3">
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{message}</p>

        <input
          type="password"
          className="input-field"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="본인 비밀번호"
          autoComplete="current-password"
          autoFocus
          disabled={isPending}
        />

        {error && (
          <p style={{ fontSize: '12px', color: 'var(--danger)' }}>{error}</p>
        )}
      </form>
    </Modal>
  );
}
